import {
  Badge,
  Box,
  ContentLayout,
  Header,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { EventInput, LocationType, ShiftInput } from '@amzn/red-velvet-api';
import { formatShiftDate } from '../../../../utils/dateFormatting';
import { useTimezone } from '../../../../hooks/timezone';
import { useLocale } from '../../../../hooks/locale';

export interface EventPreviewHeaderProps {
  currentEventFields: EventInput;
  currentShiftFields: ShiftInput[];
}

const DOT_DELIMITER = '•';

export function EventPreviewHeader({
  currentEventFields,
  currentShiftFields,
}: EventPreviewHeaderProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const locale = useLocale();
  const timezone = useTimezone();

  const getLocationBadge = () => {
    switch (currentEventFields.location?.locationType) {
      case LocationType.ON_SITE:
        return <Badge color="green">{t('eventDetailsStep.onCampus', { ns: 'events' })}</Badge>;
      case LocationType.OFF_SITE:
        return <Badge color="blue">{t('eventDetailsStep.offCampus', { ns: 'events' })}</Badge>;
      case LocationType.VIRTUAL:
        return <Badge color="grey">{t('eventDetailsStep.virtual', { ns: 'events' })}</Badge>;
      default:
        return null;
    }
  };

  const getLocationText = () => {
    const { location } = currentEventFields;
    const locationType = location?.locationType;
    const cityStateCountry = location?.city
      ? t('eventDisplay.benevityButBetterPhysicalLocation', {
          city: location.city,
          state: location.province,
          country: location.country,
        })
      : '';
    return locationType === LocationType.OFF_SITE
      ? (location?.name ?? cityStateCountry)
      : cityStateCountry;
  };

  const totalSlots = currentShiftFields.reduce((count, shift) => count + (shift.maxSignup ?? 0), 0);

  const dateRange = () => {
    if (currentShiftFields.length === 0) return { earliest: undefined, latest: undefined };

    let earliest = currentShiftFields[0].startDateTime;
    let latest = currentShiftFields[0].endDateTime ?? currentShiftFields[0].startDateTime;

    currentShiftFields.forEach((shift) => {
      if (shift.startDateTime && (!earliest || shift.startDateTime < earliest)) {
        earliest = shift.startDateTime;
      }
      const end = shift.endDateTime ?? shift.startDateTime;
      if (end && (!latest || end > latest)) {
        latest = end;
      }
    });

    return { earliest, latest };
  };

  const getDateString = () => {
    const { earliest, latest } = dateRange();
    if (!earliest) return '';
    // Format the Date directly with the locale + event timezone. Do NOT re-parse a localized date
    // string (new Date("31 mars 2026") is "Invalid Date" in most non-English locales).
    const startDate = formatShiftDate(earliest, timezone, locale);
    const latestDateStr = latest ? formatShiftDate(latest, timezone, locale) : '';
    const endDate = latestDateStr && latestDateStr !== startDate ? ` - ${latestDateStr}` : '';
    return startDate + endDate;
  };

  const locationText = getLocationText();

  const organizations = currentEventFields.communityOrganizations ?? [];

  const getPreviewContent = () => (
    <Box padding="l" data-testid="event-preview-header">
      <SpaceBetween direction="vertical" size="xxs">
        {getLocationBadge()}
        <Header variant="h1">{currentEventFields.title || ''}</Header>
        <TextContent>
          {[
            getDateString(),
            locationText,
            t('previewModal.numberOfSpots', {
              count: totalSlots,
              numShifts: currentShiftFields.length,
            }),
          ]
            .filter(Boolean)
            .join(` ${DOT_DELIMITER} `)}
        </TextContent>
        {organizations.length > 0 && (
          <TextContent>
            <strong>{t('eventDetailsStep.communityOrganization')}:</strong>
            <Box>
              {organizations.map((org, index) => (
                <span key={index}>
                  <strong>{org.name}</strong>
                  {index < organizations.length - 1 ? ', ' : ''}
                </span>
              ))}
            </Box>
          </TextContent>
        )}
        <TextContent>
          {currentEventFields.shortDescription ||
            t('eventDetailsStep.summarizedDescriptionPlaceholder')}
        </TextContent>
      </SpaceBetween>
    </Box>
  );

  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerVariant="high-contrast"
      header={getPreviewContent()}
    />
  );
}
